import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { SegmentOverview, SegmentFilters, SegmentFiltersAdmin } from '../models/segments';
import { TableSortState } from '../models/tableSortState';
import { AppContext, AppProviderDispatch } from '../shared/app-context';
import useRestService from '../shared/rest-service';

/**
 * Hook to fetch a client user a list of available segments
 */
export function useSegments(
  sort: TableSortState,
  searchTerm: string,
  activeFilters: SegmentFilters | SegmentFiltersAdmin
) {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();
  const userInterface = window.env.USER_INTERFACE != null ? window.env.USER_INTERFACE : 'default';

  return useQuery<SegmentOverview, Error>(
    ['segments', appState.state.clientId, sort, searchTerm, activeFilters],
    () => {
      let query =
        `Page=${sort.page}` +
        `&PageSize=${sort.pageSize}` +
        `&Sort=${sort.orderColumn}` +
        `&SortDirection=${sort.orderDirection}`;
      if (searchTerm) {
        query += `&SearchTerm=${searchTerm}`;
      }
      if (activeFilters.startDate) {
        query += `&StartDate=${activeFilters.startDate.toISOString()}`;
      }
      if (activeFilters.endDate) {
        query += `&EndDate=${activeFilters.endDate.toISOString()}`;
      }
      if (activeFilters.min) {
        query += `&MinRecordAmount=${activeFilters.min}`;
      }
      if (activeFilters.max) {
        query += `&MaxRecordAmount=${activeFilters.max}`;
      }
      activeFilters.users.forEach((userId) => {
        query += `&UserIds=${userId}`;
      });
      if ('teams' in activeFilters) {
        activeFilters.teams.forEach((teamId) => {
          query += `&ClientIds=${teamId}`;
        });
      }
      // Do not filter on `isShared` if 0 or both of the possible filters are selected.
      if (activeFilters.isShared.length === 1) {
        query += `&IsShared=${activeFilters.isShared[0]}`;
      }
      if (userInterface !== 'management') {
        return fetchWrapper(`api/${appState.state.clientId}/segments?${query}`);
      }
      return fetchWrapper(`api/segments?${query}`);
    },
    {
      keepPreviousData: true
    }
  );
}
